import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import type { Terminal } from '@xterm/xterm';
import { ArrowLeft, Users, Eye, Pencil, History } from 'lucide-react';
import { useQuery } from 'react-query';
import { getCookie } from '../utils/cookies';
import { apiPath, authTenantHeaders, getSocketUrl, wsTenantParam } from '../services/api';
import { Breadcrumbs, Button, Card, IconButton, MonoCell, PageHeader, StatusBadge } from '@/components/ds-zta';
import { errorMessage, fetchJson } from '@/lib/http';
import TerminalSurface from '@/components/terminal/TerminalSurface';
import { decodeJoinMessage, forwardPeerInput, joinSocketUrl, parseJoinMode } from '@/components/terminal/liveSession';

const sessionApi = {
    getParticipants: (id: string) =>
        fetchJson(apiPath(`/api/v1/sessions/${encodeURIComponent(id)}/participants`), { credentials: 'include', headers: authTenantHeaders() }),
};

export default function SessionJoinPage() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const wsRef = useRef<WebSocket | null>(null);
    const termRef = useRef<Terminal | null>(null);
    // Output that arrives before the terminal is ready is held, then written.
    const pendingRef = useRef('');
    const [hasOutput, setHasOutput] = useState(false);
    const [connected, setConnected] = useState(false);
    // Watch (observer, read-only) unless the link asked for peer (Join).
    const mode = parseJoinMode(searchParams.get('mode'));

    const { data: participantsData, isError: participantsError, error: participantsErr, refetch: refetchParticipants } = useQuery(
        ['session-participants', id],
        () => sessionApi.getParticipants(id!),
        { enabled: !!id, refetchInterval: 5000 }
    );

    const writeOutput = useCallback((text: string) => {
        if (!text) return;
        setHasOutput(true);
        if (termRef.current) termRef.current.write(text);
        else pendingRef.current += text;
    }, []);

    useEffect(() => {
        if (!id) return;

        const ws = new WebSocket(joinSocketUrl(getSocketUrl(), id, mode, getCookie('auth_token'), wsTenantParam()));
        wsRef.current = ws;

        ws.onopen = () => {
            setConnected(true);
            refetchParticipants();
        };

        // Output goes to an xterm screen, so colours, cursor moves and clears
        // are drawn rather than printed as raw escape codes (design eval F19).
        ws.onmessage = (event) => {
            const msg = decodeJoinMessage(event.data);
            if (msg.kind === 'output') writeOutput(msg.text);
            else if (msg.kind === 'participants') refetchParticipants();
        };

        ws.onclose = () => setConnected(false);
        ws.onerror = () => setConnected(false);

        return () => {
            ws.close();
            wsRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, mode]);

    const handleReady = useCallback((term: Terminal) => {
        termRef.current = term;
        if (pendingRef.current) {
            term.write(pendingRef.current);
            pendingRef.current = '';
        }
    }, []);

    // Peer input: every sequence xterm produces (Enter, Backspace, Tab,
    // arrows, Ctrl keys, paste), not only printable keys (design eval F19).
    const handleData = useCallback((data: string) => {
        forwardPeerInput(mode, wsRef.current, data);
    }, [mode]);

    const participants = participantsData?.participants || [];

    return (
        <div className="flex flex-col h-[calc(100vh-120px)]">
            <PageHeader
                className="mb-4"
                icon={<History />}
                breadcrumbs={
                    <div className="flex items-center gap-2">
                        <IconButton aria-label="Back to sessions" size="sm" icon={<ArrowLeft />} onClick={() => navigate('/sessions')} />
                        <Breadcrumbs
                            items={[{ label: 'Sessions', href: '/sessions' }, { label: 'Live session' }]}
                            renderLink={(item) => <Link to={item.href!} className="rc-breadcrumbs__link">{item.label}</Link>}
                        />
                    </div>
                }
                title="Live Session"
                description={
                    <span className="flex items-center gap-3">
                        <StatusBadge value={connected ? 'connected' : 'disconnected'} shape="chip">
                            {connected ? 'Connected' : 'Disconnected'}
                        </StatusBadge>
                        {id && (
                            <span className="flex min-w-0 items-center gap-1">
                                Session <MonoCell value={id} width="10rem" />
                            </span>
                        )}
                        <span className="flex items-center gap-1">
                            {mode === 'peer' ? <Pencil className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                            {mode === 'peer' ? 'Peer (can type)' : 'Observer (read-only)'}
                        </span>
                    </span>
                }
                secondaryActions={
                    <span className="flex items-center gap-2 text-sm text-[var(--d2d-text-muted)]">
                        <Users className="h-4 w-4" />
                        {participants.length} participant{participants.length !== 1 ? 's' : ''}
                    </span>
                }
            />

            {/* Terminal + Sidebar */}
            <div className="flex flex-1 gap-4 min-h-0">
                {/* Terminal */}
                <div className="zta-term-chrome relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-[var(--d2d-border)]">
                    <TerminalSurface
                        className="flex-1 p-1"
                        readOnly={mode !== 'peer'}
                        autoFocus={mode === 'peer'}
                        aria-label={mode === 'peer' ? 'Live session terminal (you can type)' : 'Live session terminal (read-only)'}
                        onReady={handleReady}
                        onDispose={() => { termRef.current = null; }}
                        onData={handleData}
                    />
                    {!hasOutput && (
                        <p className="pointer-events-none absolute left-4 top-3 m-0 font-mono text-sm text-[var(--d2d-text-muted)]">
                            Waiting for session data…
                        </p>
                    )}
                </div>

                {/* Participants Sidebar */}
                <Card className="w-48 shrink-0 p-3 overflow-auto">
                    <h3 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase mb-2">Participants</h3>
                    <div className="space-y-2">
                        {participants.map((p: any, i: number) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                                <StatusBadge value="connected" className="min-w-0 truncate text-[var(--d2d-text-primary)]">{p.user_name || p.user_id}</StatusBadge>
                                <span className="text-xs text-[var(--d2d-text-muted)] ml-auto">
                                    {p.mode === 'peer'
                                        ? <Pencil className="h-3.5 w-3.5" aria-label="Peer (can type)" />
                                        : <Eye className="h-3.5 w-3.5" aria-label="Observer (view only)" />}
                                </span>
                            </div>
                        ))}
                        {participantsError ? (
                            <div className="space-y-2" role="alert">
                                <p className="text-xs text-[var(--danger-text)]">
                                    {participants.length > 0 ? 'List may be out of date. ' : ''}
                                    {errorMessage(participantsErr, 'Could not load participants')}
                                </p>
                                <Button size="sm" variant="secondary" onClick={() => refetchParticipants()}>Retry</Button>
                            </div>
                        ) : participants.length === 0 && (
                            <p className="text-xs text-[var(--d2d-text-muted)]">No participants yet</p>
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );
}
